/**
 * Momently-style active-interaction timer.
 *
 * Accumulates, purely client-side via performance.now(), how long the reader
 * has been "actively" reading: the document is visible, the window has
 * focus, and a qualifying interaction (or view initialization) happened
 * within the last idleTimeoutMsec. No network calls happen while this runs --
 * it is read synchronously by the caller at flush time.
 *
 * Every listener that counts as an interaction is bound to the same
 * zero-argument markActivity() function -- nothing is ever read off the
 * triggering event (no target, key, pointer coordinates, or selected text).
 */

const DEFAULT_ACTIVE_IDLE_TIMEOUT_MSEC = 5000;

export interface CreateActiveInteractionTimerOptions {
  idleTimeoutMsec?: number;
  /** Injectable clock, for tests. Defaults to performance.now() (falling back
   * to Date.now() where performance is unavailable). */
  now?: () => number;
}

export interface ActiveInteractionTimerDebugState {
  visible: boolean;
  focused: boolean;
  consented: boolean;
  withinIdleWindow: boolean;
  counting: boolean;
  elapsedMsec: number;
}

export interface ActiveInteractionTimer {
  start(): void;
  resetForNewView(): void;
  setConsentGranted(granted: boolean): void;
  getElapsedMsec(): number;
  /** The idle timeout actually in effect (the configured override, or the
   * module's own default when none was given) -- lets a caller report it
   * (e.g. as an event param) without needing to know or import the default. */
  getIdleTimeoutMsec(): number;
  destroy(): void;
  getDebugState(): ActiveInteractionTimerDebugState;
}

function defaultNow(): number {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

export function createActiveInteractionTimer(
  options: CreateActiveInteractionTimerOptions = {}
): ActiveInteractionTimer {
  const idleTimeoutMsec =
    typeof options.idleTimeoutMsec === "number" && options.idleTimeoutMsec > 0
      ? options.idleTimeoutMsec
      : DEFAULT_ACTIVE_IDLE_TIMEOUT_MSEC;
  const now = typeof options.now === "function" ? options.now : defaultNow;

  let attached = false;
  let destroyed = false;
  let visible = true;
  let focused = true;
  let consented = false;
  let withinIdleWindow = false;
  let accumulatedMsec = 0;
  let segmentStartTime: number | null = null;
  let idleTimeoutHandle: ReturnType<typeof setTimeout> | null = null;

  function closeOpenSegment() {
    if (segmentStartTime !== null) {
      accumulatedMsec += Math.max(0, now() - segmentStartTime);
      segmentStartTime = null;
    }
  }

  function recomputeCounting() {
    if (destroyed) return;
    const shouldCount = visible && focused && consented && withinIdleWindow;
    if (shouldCount && segmentStartTime === null) {
      segmentStartTime = now();
    } else if (!shouldCount) {
      closeOpenSegment();
    }
  }

  function bumpIdleWindow() {
    if (destroyed) return;
    withinIdleWindow = true;
    if (idleTimeoutHandle !== null) clearTimeout(idleTimeoutHandle);
    idleTimeoutHandle = setTimeout(() => {
      idleTimeoutHandle = null;
      withinIdleWindow = false;
      recomputeCounting();
    }, idleTimeoutMsec);
    recomputeCounting();
  }

  function markActivity() {
    bumpIdleWindow();
  }

  function onVisibilityChange() {
    if (destroyed || typeof document === "undefined") return;
    visible = document.visibilityState === "visible";
    recomputeCounting();
  }

  function onFocus() {
    if (destroyed) return;
    focused = true;
    recomputeCounting();
  }

  function onBlur() {
    if (destroyed) return;
    focused = false;
    recomputeCounting();
  }

  function start() {
    if (attached || destroyed || typeof window === "undefined") return;
    attached = true;
    visible = typeof document === "undefined" || document.visibilityState === "visible";
    focused =
      typeof document === "undefined" || typeof document.hasFocus !== "function"
        ? true
        : document.hasFocus();
    // capture: true so activity anywhere on the page counts, even inside an
    // element that stops propagation before a bubble-phase listener on
    // window would see it.
    window.addEventListener("scroll", markActivity, { passive: true, capture: true });
    window.addEventListener("click", markActivity, { capture: true });
    window.addEventListener("pointerdown", markActivity, { capture: true });
    window.addEventListener("keydown", markActivity, { capture: true });
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
      document.addEventListener("selectionchange", markActivity);
    }
  }

  function resetForNewView() {
    segmentStartTime = null;
    accumulatedMsec = 0;
    // "Active for 5 seconds after page initialization" is the same trigger
    // as a real interaction.
    bumpIdleWindow();
  }

  function setConsentGranted(granted: boolean) {
    if (destroyed) return;
    consented = granted;
    recomputeCounting();
  }

  function getElapsedMsec(): number {
    if (segmentStartTime === null) return accumulatedMsec;
    return accumulatedMsec + Math.max(0, now() - segmentStartTime);
  }

  function getIdleTimeoutMsec(): number {
    return idleTimeoutMsec;
  }

  function destroy() {
    if (destroyed) return;
    // Force-close any open segment regardless of shouldCount -- once
    // destroyed, getElapsedMsec() must stop advancing forever, not just stop
    // reacting to future input changes.
    closeOpenSegment();
    destroyed = true;
    if (idleTimeoutHandle !== null) {
      clearTimeout(idleTimeoutHandle);
      idleTimeoutHandle = null;
    }
    if (!attached) return;
    attached = false;
    if (typeof window !== "undefined") {
      window.removeEventListener("scroll", markActivity, { capture: true });
      window.removeEventListener("click", markActivity, { capture: true });
      window.removeEventListener("pointerdown", markActivity, { capture: true });
      window.removeEventListener("keydown", markActivity, { capture: true });
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
    }
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      document.removeEventListener("selectionchange", markActivity);
    }
  }

  function getDebugState(): ActiveInteractionTimerDebugState {
    return {
      visible,
      focused,
      consented,
      withinIdleWindow,
      counting: segmentStartTime !== null,
      elapsedMsec: getElapsedMsec(),
    };
  }

  return {
    start,
    resetForNewView,
    setConsentGranted,
    getElapsedMsec,
    getIdleTimeoutMsec,
    destroy,
    getDebugState,
  };
}
